import {useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {Spinner, useBreakpointValue, useDisclosure, useToast} from "@chakra-ui/react";
import {io} from "socket.io-client";
import {useDispatch, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import axios from "axios";
import {useAuth} from "../../redux/AuthProvider";
import {setVideos} from "../../redux/reducers";
import {getSessionTokenVideo} from "../../utils/pdf-page-helpers";
import {MySpinnerLarge} from "../../components/Reuseable/MySpinner";
import PricingScreen from "../../components/Modals/Payments/PricingScreen";
import PDFPageVideoGemini from "./PDFPageVideoGemini";
import {useServerIP} from "../../redux/ServerIPContext";

export default function WrapperComponentVideoGemini ()  {
    const auth = useAuth();
    const toast = useToast();
    const [socket, setSocket] = useState(null);
    const { id } = useParams();
    const [sessionToken, setSessionToken] = useState("")
    const [geminiSessionToken, setGeminiSessionToken] = useState("")
    const [geminiPollingKey, setGeminiPollingKey] = useState("")
    const [geminiFileResource, setGeminiFileResource] = useState("")
    const [currentDocument, setCurrentDocument] = useState(null);
    const { serverIPs, setServerIPs, isDev } = useServerIP();

    const documents = useSelector(state => state.documents.savedVideos)

    const [forceRender, setForceRender] = useState(0);
    const [socketID, setSocketID] = useState("");
    const { isTypingVideo, setIsTypingVideo } = useAuth();
    const {isThinkingVideo, setIsThinkingVideo} = useAuth();
    const dispatch = useDispatch();
    const { isOpen: isOpenPricingScreen, onOpen: onOpenPricingScreen, onClose: onClosePricingScreen } = useDisclosure();
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base';

    // Initialize socket connection
    useEffect( () => {
        console.log("In UseEffect WrapperComponentVideoGemini with id:", id);

        const randomID = uuidv4();
        let userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111"

        if (userID === "11111111-1111-1111-1111-111111111111") {
            userID = userID + randomID;
        }
        console.log("USERID guest with random ID", userID)

        const newSocket = io(serverIPs.SERVER_IP_SOCKET, {
            query: { user_id: userID }
        });

        setSocket(newSocket);
        console.log("Socket:", socket)
        newSocket.on('connected', function(data) {
            console.log("Connected:", data);
            setSocketID("user_" + userID);
        });
        return () => {
            console.log("Socket disconnecting", newSocket)
            newSocket.disconnect();
        }
    }, [id])

    // Find current document
    useEffect(() => {
        console.log("In UseEffect WrapperComponentVideoGemini with socket:", socket);
        if (documents.length > 0) {
            console.log("Documents length > 0")
            setCurrentDocument(documents.find((document) => document.id === id));
            setForceRender(prevForceRender => prevForceRender + 1);
            dispatch(setVideos(documents));
        }
    }, [documents.length, id]);

    // Initialize Gemini video session
    useEffect(() => {
        console.log("In UseEffect WrapperComponentVideoGemini with currentDocument:", currentDocument);
        if (currentDocument) {
            console.log("CurrentDocument is not null, initializing Gemini session")
            const initializeGeminiSession = async () => {
                const userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111"
                console.log("USERID into gemini", userID)
                console.log("ID into gemini", id)

                try {
                    // First, get the regular session token for the original video
                    const sessionResponse = await getSessionTokenVideo(id, userID, serverIPs.SERVER_IP_VIDEO);
                    if (sessionResponse) {
                        setSessionToken(sessionResponse.sessionToken);
                        console.log("Got session token:", sessionResponse.sessionToken);
                    }

                    // Start Gemini video session
                    const geminiResponse = await axios.post(serverIPs.SERVER_IP_VIDEO + '/videos/start_video_session_gemini', {
                        file_id: id,
                        user_id: userID
                    });

                    const geminiData = geminiResponse.data;
                    console.log("Gemini session response:", geminiData);

                    if (geminiData.message.polling_key) {
                        setGeminiPollingKey(geminiData.message.polling_key);
                        console.log("Got Gemini polling key:", geminiData.message.polling_key);

                        // Store the Gemini session token
                        if (geminiData.message.session_token) {
                            setGeminiSessionToken(geminiData.message.session_token);
                            console.log("Got Gemini session token:", geminiData.message.session_token);
                        }

                        // Start polling for Gemini processing status
                        pollGeminiProgress(geminiData.message.polling_key);
                    }
                } catch (error) {
                    console.error("Error initializing Gemini session:", error);
                    toast({
                        title: "Error initializing Gemini session",
                        description: "Please try again later",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            };
            initializeGeminiSession();
        }
    }, [currentDocument]);

    // Poll for Gemini video processing status
    const pollGeminiProgress = async (pollingKey) => {
        const maxAttempts = 120; // 10 minutes with 5-second intervals
        let attempts = 0;

        const poll = async () => {
            try {
                console.log("Polling Gemini progress with key:", pollingKey);
                const response = await axios.post(serverIPs.SERVER_IP + "/general_progress", {
                    key: pollingKey
                });

                console.log("Gemini progress response:", response.data);

                // Check if we got the file resource location
                if (response.data.message && response.data.message.startsWith("files/")) {
                    console.log("Gemini video processing complete! File resource:", response.data.message);
                    setGeminiFileResource(response.data.message);
                    return;
                }

                // Continue polling if not ready yet
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, 5000); // Poll every 5 seconds
                } else {
                    console.error("Gemini processing timeout");
                    toast({
                        title: "Gemini processing timeout",
                        description: "Video processing is taking longer than expected",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error polling Gemini progress:", error);
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, 5000);
                }
            }
        };

        poll();
    };

    return (
        geminiFileResource === "" ?
            <div style={{justifyContent:'center', alignItems:'center', display:'flex', height:'100%', width:'100%', paddingTop:"5rem"}}>
                <div style={{textAlign: 'center'}}>
                    <MySpinnerLarge/>
                    <div style={{marginTop: '3.5rem', fontSize: '20px', color: '#666', marginLeft:'-6rem'}}>
                        Processing visual input of video...
                    </div>
                </div>
            </div>
            :
            <div>
                <PDFPageVideoGemini
                    document={documents.find((document) => document.id === id)}
                    isMobile={isMobile}
                    key={forceRender}
                    documents={documents}
                    id={id}
                    isLoggedIn={auth.isLoggedIn}
                    sessionToken={sessionToken}
                    geminiSessionToken={geminiSessionToken}
                    geminiPollingKey={geminiPollingKey}
                    geminiFileResource={geminiFileResource}
                    socketID={socketID}
                    socket={socket}
                    isTyping={isTypingVideo}
                    setIsTyping={setIsTypingVideo}
                    isThinking={isThinkingVideo}
                    setIsThinking={setIsThinkingVideo}
                    toast={toast}
                    serverIP={serverIPs.SERVER_IP_VIDEO}
                />
                <PricingScreen isOpenPricingScreen={isOpenPricingScreen}
                               onClosePricingScreen={onClosePricingScreen}
                               onOpenPricingScreen={onOpenPricingScreen}/>
            </div>
    )
}
